import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { Avatar, Button, EmptyState, Icon, PageBody, SkeletonText, useToast } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import "./org-chart.css";

export type OrgNode = {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  jobTitle: string | null;
  department: string | null;
  managerId: string | null;
};

type OrgTree = OrgNode & { reports: OrgTree[] };

const PAGE = 200;

/**
 * Turn the flat list into a forest. People whose manager is not in the list
 * (outside the office, or not loaded) are roots. A cycle, which the API
 * refuses to create, would leave its members unreachable from any root, so
 * they are lifted to roots rather than silently dropped.
 */
export function buildOrgTree(nodes: OrgNode[]): OrgTree[] {
  const byId = new Map<string, OrgTree>(nodes.map((n) => [n.id, { ...n, reports: [] }]));
  const roots: OrgTree[] = [];
  for (const node of byId.values()) {
    const parent = node.managerId ? byId.get(node.managerId) : undefined;
    if (parent && parent.id !== node.id) parent.reports.push(node);
    else roots.push(node);
  }

  const reachable = new Set<string>();
  const walk = (n: OrgTree) => {
    if (reachable.has(n.id)) return;
    reachable.add(n.id);
    n.reports.forEach(walk);
  };
  roots.forEach(walk);
  for (const node of byId.values()) {
    if (!reachable.has(node.id)) {
      // Break the loop at this node so the rendered tree stays finite.
      const parent = node.managerId ? byId.get(node.managerId) : undefined;
      if (parent) parent.reports = parent.reports.filter((r) => r.id !== node.id);
      roots.push(node);
      walk(node);
    }
  }

  const sort = (list: OrgTree[]) => {
    list.sort((a, b) => a.displayName.localeCompare(b.displayName));
    list.forEach((n) => sort(n.reports));
  };
  sort(roots);
  return roots;
}

function countAll(node: OrgTree): number {
  return node.reports.reduce((sum, r) => sum + 1 + countAll(r), 0);
}

function TreeNode({ node, depth, open, onToggle }: { node: OrgTree; depth: number; open: Set<string>; onToggle: (id: string) => void }) {
  const expanded = open.has(node.id);
  const hasReports = node.reports.length > 0;
  return (
    <li role="treeitem" aria-expanded={hasReports ? expanded : undefined} aria-level={depth + 1}>
      <div className="org-row" style={{ paddingLeft: depth * 20 }}>
        {hasReports ? (
          <button
            type="button"
            className="org-row__toggle"
            aria-label={`${expanded ? "Collapse" : "Expand"} ${node.displayName}`}
            onClick={() => onToggle(node.id)}
          >
            <Icon name={expanded ? "chevron-down" : "chevron-right"} size={14} />
          </button>
        ) : (
          <span className="org-row__toggle" aria-hidden="true" />
        )}
        <Avatar name={node.displayName} src={node.avatarUrl ?? undefined} size={28} />
        <Link className="org-row__name" to={`/people?person=${node.id}`}>
          {node.displayName}
        </Link>
        <span className="org-row__meta">{[node.jobTitle, node.department].filter(Boolean).join(" · ")}</span>
        {hasReports && <span className="org-row__count">{countAll(node)} below</span>}
      </div>
      {hasReports && expanded && (
        <ul role="group" className="org-tree">
          {node.reports.map((child) => (
            <TreeNode key={child.id} node={child} depth={depth + 1} open={open} onToggle={onToggle} />
          ))}
        </ul>
      )}
    </li>
  );
}

export function OrgChart() {
  usePageTitle("Org chart");
  const { toast } = useToast();
  const [nodes, setNodes] = useState<OrgNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<Set<string>>(new Set());

  // The API pages the list; the tree needs everyone, so walk every page.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const all: OrgNode[] = [];
      for (let offset = 0; ; offset += PAGE) {
        const res = await api.get<{ people: OrgNode[]; hasMore: boolean }>(`/org-chart?limit=${PAGE}&offset=${offset}`);
        all.push(...res.people);
        if (!res.hasMore || cancelled) break;
      }
      if (!cancelled) setNodes(all);
    })()
      .catch((error: unknown) => {
        if (!cancelled) toast(error instanceof ApiError ? error.message : "Could not load the org chart.", { tone: "danger" });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const roots = useMemo(() => buildOrgTree(nodes), [nodes]);

  const toggle = useCallback((id: string) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = () => setOpen(new Set(nodes.map((n) => n.id)));

  return (
    <PageBody title="Org chart" width="regular">
      {loading ? (
        <SkeletonText lines={6} />
      ) : roots.length === 0 ? (
        <EmptyState icon="people" title="Nobody here yet" description="People appear once they join this office." />
      ) : (
        <>
          <div className="org-actions">
            <Button variant="secondary" size="sm" onClick={expandAll}>
              Expand all
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setOpen(new Set())}>
              Collapse all
            </Button>
          </div>
          <ul role="tree" aria-label="Reporting lines" className="org-tree org-tree--root">
            {roots.map((root) => (
              <TreeNode key={root.id} node={root} depth={0} open={open} onToggle={toggle} />
            ))}
          </ul>
        </>
      )}
    </PageBody>
  );
}
